/**
 * Flex monthly plan write/read service — thin wrappers over the SECURITY DEFINER RPCs.
 *
 * The save path re-enforces the lock server-side and stamps `updated_by`; callers never
 * write flex_month_plans directly. Reads go through plain RLS-scoped selects.
 */
import { supabase } from '../lib/supabase';
import { analytics } from '../utils/analytics';
import type { FlexMonthPlan } from '../types/program';

export interface SeedResult {
  plan_month: string;
  eligible: number;
  seeded: number;
}

/**
 * Save (insert or update) a draft plan for one (enrollment, month). Server enforces the
 * lock and caller authorization. `allowLocked` is honored only for admins (the post-lock
 * new-enrollee path); ignored for parents.
 */
export async function saveFlexPlan(
  enrollmentId: string,
  planMonth: string,
  dates: string[],
  opts: {
    allowLocked?: boolean;
    /**
     * The day set being replaced, and who is replacing it. Both portals call this function,
     * so the audit event is emitted here rather than at each call site — but only the caller
     * knows what was on screen before the edit.
     */
    previousDates?: string[] | null;
    actorType?: 'parent' | 'admin';
  } = {},
): Promise<FlexMonthPlan> {
  const { data, error } = await supabase.rpc('flex_plan_save', {
    p_enrollment_id: enrollmentId,
    p_plan_month: planMonth,
    p_dates: dates,
    p_allow_locked: opts.allowLocked ?? false,
  });
  if (error) throw new Error(error.message);

  const plan = data as unknown as FlexMonthPlan;
  analytics.trackFlexPlanSave({
    enrollmentId,
    planMonth,
    dates,
    previousDates: opts.previousDates,
    status: plan?.status ?? null,
    allowLocked: opts.allowLocked,
    actorType: opts.actorType,
  });
  return plan;
}

/**
 * Admin-only: seed draft plans for active flex enrollments lacking a plan for the target
 * month (default: next calendar month). Returns counts.
 */
export async function seedNextMonthFlexPlans(planMonth?: string): Promise<SeedResult> {
  const { data, error } = await supabase.rpc('flex_seed_next_month_plans', {
    p_plan_month: planMonth ?? undefined,
  });
  if (error) throw new Error(error.message);
  return data as unknown as SeedResult;
}

/**
 * Persist the first-month plan AND create the first-month invoice at enrollment (parent or
 * admin), server-side and atomically. Bypasses the editing lock — the starting month is
 * always settable when you enroll — commits the plan as locked, and bills the confirmed
 * days × the per-day rate (+ one-time materials, discounts, floored at $0). `suppressEmail`
 * (admin enroll-on-behalf) holds the invoice-created email. 0 days → plan 'skipped', no
 * invoice.
 */
export async function persistFirstMonthPlan(
  enrollmentId: string,
  planMonth: string,
  dates: string[],
  opts: { suppressEmail?: boolean } = {},
): Promise<FlexMonthPlan> {
  const { data, error } = await supabase.rpc('flex_enroll_persist_plan', {
    p_enrollment_id: enrollmentId,
    p_plan_month: planMonth,
    p_dates: dates,
    p_suppress_email: opts.suppressEmail ?? false,
  });
  if (error) throw new Error(error.message);
  return data as unknown as FlexMonthPlan;
}

export interface ReminderResult {
  plan_month: string;
  eligible: number;
  sent: number;
}

/**
 * Admin-only: send the pre-lock reminder to flex parents for the next month's plan.
 * Invokes the flex-plan-reminders edge function.
 */
export async function sendFlexReminders(planMonth?: string): Promise<ReminderResult> {
  const { data, error } = await supabase.functions.invoke('flex-plan-reminders', {
    body: planMonth ? { plan_month: planMonth } : {},
  });
  if (error) throw new Error(error.message);
  return data as ReminderResult;
}

/** Fetch a single flex plan for an (enrollment, month), or null if none exists yet. */
export async function getFlexPlan(
  enrollmentId: string,
  planMonth: string,
): Promise<FlexMonthPlan | null> {
  const { data, error } = await supabase
    .from('flex_month_plans')
    .select('*')
    .eq('enrollment_id', enrollmentId)
    .eq('plan_month', planMonth)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as FlexMonthPlan | null) ?? null;
}

/** Fetch all flex plans for an enrollment, newest month first (for the history view). */
export async function listFlexPlans(enrollmentId: string): Promise<FlexMonthPlan[]> {
  const { data, error } = await supabase
    .from('flex_month_plans')
    .select('*')
    .eq('enrollment_id', enrollmentId)
    .order('plan_month', { ascending: false });
  if (error) throw new Error(error.message);
  return (data as FlexMonthPlan[] | null) ?? [];
}
